import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineDot
} from "@mui/lab";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";

type StreamEvent = {
  timestamp: string;
  type: string;
  message: string;
  color?: string;
  details?: string;
};

type StreamEventsResponse = {
  streamId?: string;
  events?: StreamEvent[];
  error?: string;
};

type StreamEventsTimelineProps = {
  streamId: string;
  clearSignal?: number;
  showAllSignal?: number;
  onTotalEventsChange?: (count: number) => void;
  disabledErrorMessages?: string[];
};

const StreamEventsTimeline = ({
  streamId,
  clearSignal,
  showAllSignal,
  onTotalEventsChange,
  disabledErrorMessages
}: StreamEventsTimelineProps) => {
  const { authorizedFetch, user } = useAuth();
  const theme = useTheme();
  const [clearedAtMs, setClearedAtMs] = useState<number | null>(null);
  const lastClearSignalRef = useRef<number | undefined>(undefined);
  const lastShowAllSignalRef = useRef<number | undefined>(undefined);

  const clearStorageKey = useMemo(() => {
    const userKey = user?.id?.trim() || user?.username?.trim() || "anonymous";
    return `sky-view.stream-events-cleared-at:${userKey}:${streamId}`;
  }, [streamId, user?.id, user?.username]);

  const resolveEventColor = useCallback(
    (type: string) => {
      const normalized = type.trim().toLowerCase();
      if (normalized.includes("error") || normalized.includes("fail")) {
        return theme.palette.error.main;
      }
      if (normalized.includes("warn")) {
        return theme.palette.warning.main;
      }
      if (normalized.includes("success") || normalized.includes("start") || normalized.includes("online")) {
        return theme.palette.success.main;
      }
      if (normalized.includes("info") || normalized.includes("update")) {
        return theme.palette.info.main;
      }
      return theme.palette.primary.main;
    },
    [theme.palette.error.main, theme.palette.info.main, theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main]
  );

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }),
    []
  );

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "2-digit"
      }),
    []
  );

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery<StreamEvent[]>({
    queryKey: ["stream-events", streamId],
    enabled: Boolean(streamId),
    refetchInterval: 1000,
    queryFn: async () => {
      const response = await authorizedFetch(`/api/streams/${encodeURIComponent(streamId)}/events`);
      const payload = (await response.json().catch(() => null)) as StreamEventsResponse | null;
      if (!response.ok) {
        const message = payload?.error ?? "Unable to load stream events";
        throw new Error(message);
      }
      return payload?.events ?? [];
    }
  });
  const events = data ?? [];
  const disabledErrorSet = useMemo(
    () =>
      new Set(
        (disabledErrorMessages ?? [])
          .map((message) => message.trim())
          .filter((message) => message.length > 0)
      ),
    [disabledErrorMessages]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const raw = window.localStorage.getItem(clearStorageKey);
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    setClearedAtMs(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
  }, [clearStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (typeof clearSignal !== "number") {
      return;
    }
    if (lastClearSignalRef.current === undefined) {
      lastClearSignalRef.current = clearSignal;
      return;
    }
    if (clearSignal === lastClearSignalRef.current) {
      return;
    }
    lastClearSignalRef.current = clearSignal;
    const now = Date.now();
    setClearedAtMs(now);
    window.localStorage.setItem(clearStorageKey, String(now));
  }, [clearSignal, clearStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (typeof showAllSignal !== "number") {
      return;
    }
    if (lastShowAllSignalRef.current === undefined) {
      lastShowAllSignalRef.current = showAllSignal;
      return;
    }
    if (showAllSignal === lastShowAllSignalRef.current) {
      return;
    }
    lastShowAllSignalRef.current = showAllSignal;
    window.localStorage.removeItem(clearStorageKey);
    setClearedAtMs(null);
  }, [showAllSignal, clearStorageKey]);

  useEffect(() => {
    if (!onTotalEventsChange) {
      return;
    }
    onTotalEventsChange(events.length);
  }, [events.length, onTotalEventsChange]);

  if (!streamId) {
    return null;
  }

  if (isLoading && !data) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    const message = error instanceof Error ? error.message : "Unable to load stream events";
    return <Alert severity="error">{message}</Alert>;
  }

  const baseVisibleEvents =
    clearedAtMs === null
      ? events
      : events.filter((event) => {
          const timestampMs = new Date(event.timestamp).getTime();
          return Number.isFinite(timestampMs) && timestampMs > clearedAtMs;
        });
  const visibleEvents = baseVisibleEvents.filter((event) => !disabledErrorSet.has(event.message.trim()));

  if (visibleEvents.length === 0) {
    const emptyMessage =
      baseVisibleEvents.length === 0
        ? events.length === 0
          ? "No events recorded for this stream yet."
          : "Events cleared. New events will appear here."
        : "No events match current filters.";

    return (
      <Stack spacing={1} py={2} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Stack>
    );
  }

  const sortedEvents = [...visibleEvents].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  return (
    <Timeline
      position="right"
      sx={{
        p: 0,
        "& .MuiTimelineItem-root:before": {
          flex: 0,
          padding: 0
        }
      }}
    >
      {sortedEvents.map((event, index) => {
        const timestamp = new Date(event.timestamp);
        const formattedTime = timeFormatter.format(timestamp);
        const tooltipLabel = dateFormatter.format(timestamp);
        const isLast = index === sortedEvents.length - 1;
        const isKlvEvent = event.message.trim().toUpperCase().startsWith("KLV");
        const explicitColor = event.color?.trim();
        const dotColor = explicitColor ? explicitColor : resolveEventColor(event.type);
        const details = event.details?.trim() ?? "";

        const messageNode = (
          <Typography variant="body2" color="text.secondary">
            {event.message}
          </Typography>
        );
        return (
          <TimelineItem key={`${event.timestamp}-${index}`}>
            <Tooltip title={tooltipLabel} placement="top">
              <TimelineOppositeContent
                color="text.secondary"
                sx={{
                  flex: 0,
                  minWidth: 96,
                  fontSize: "0.75rem",
                  pr: 1.5,
                  whiteSpace: "nowrap",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  py: 1
                }}
              >
                {formattedTime}
              </TimelineOppositeContent>
            </Tooltip>
            <TimelineSeparator>
              <TimelineDot
                variant={isKlvEvent ? "outlined" : "filled"}
                sx={{
                  bgcolor: isKlvEvent ? "transparent" : dotColor,
                  borderColor: dotColor,
                  borderWidth: isKlvEvent ? 2 : 1
                }}
              />
              {isLast ? null : <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: 1, flex: 1, ml: 1.5 }}>
              {details ? (
                <Tooltip title={details} placement="top-start">
                  {messageNode}
                </Tooltip>
              ) : (
                messageNode
              )}
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export default StreamEventsTimeline;
